import { Link } from 'react-router-dom';
import { Card } from '../../components/Card/Card';
import { ProgressBar } from '../../components/ProgressBar/ProgressBar';
import { SegmentedToggle } from '../../components/SegmentedToggle/SegmentedToggle';
import { useUiStore } from '../../store/ui';
import { useYearBundle } from '../../store/queryHooks';
import { kr } from '../../lib/format';
import { deriveOverblik } from './derive';
import styles from './OverblikPage.module.css';

export function OverblikPage() {
  const { year, period, setPeriod } = useUiStore();
  const bundle = useYearBundle(year);
  // Overblik reflects "today" — the "Denne måned" / "Næste måned" toggle is
  // absolute (calendar-based), not tied to the month selected on /budget.
  const currentMonthIdx = new Date().getMonth();

  if (bundle.isPending) return <div>Indlæser…</div>;
  if (bundle.isError || !bundle.data) return <div>Kunne ikke hente året.</div>;

  const d = deriveOverblik(bundle.data, currentMonthIdx, period);

  return (
    <div className={styles.wrap}>
      {/* Single accent KPI: Til forbrug (500 kr/day × days-to-payday) */}
      <div className={styles.kpiRow}>
        <Card compact className={`${styles.kpi} ${styles.kpiAccent}`}>
          <div className={styles.kpiLabel}>Til forbrug</div>
          <div className={styles.kpiValue}>{kr(d.forbrugsramme)}</div>
          <div className={styles.kpiSub}>{d.forbrugsrammeSub}</div>
        </Card>
      </div>

      {/* Savings summary — Opsparinger + Depoter */}
      <Card>
        <div className={styles.sectionHead} style={{ marginBottom: 18 }}>
          <h2 className={styles.sectionTitle}>Opsparing i alt</h2>
          <span
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '22px',
              color: 'var(--accent-dark)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {kr(d.savingsGrandTotal)}
          </span>
        </div>

        <div className={styles.savingsGroupHead}>
          <div className={styles.savingsGroupLabel}>Opsparinger</div>
          <div className={styles.savingsGroupTotal}>
            I alt <strong>{kr(d.opsparingerTotal)}</strong>
          </div>
        </div>
        <div className={styles.savingsGrid}>
          {d.opsparingerItems.map((s) => (
            <div key={`ops-${s.label}`} className={styles.savingsItem}>
              <div className={styles.savingsLabel}>{s.label}</div>
              <div className={styles.savingsValue}>{kr(s.value)}</div>
            </div>
          ))}
        </div>

        <div className={styles.savingsGroupHead} style={{ marginTop: 22 }}>
          <div className={styles.savingsGroupLabel}>Depoter</div>
          <div className={styles.savingsGroupTotal}>
            I alt <strong>{kr(d.depoterTotal)}</strong>
          </div>
        </div>
        <div className={styles.savingsGrid}>
          {d.depoterItems.map((d2) => (
            <div key={`dep-${d2.label}`} className={styles.savingsItem}>
              <div className={styles.savingsLabel}>{d2.label}</div>
              <div className={styles.savingsValue}>{kr(d2.value)}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Forventede faste udgifter + guide */}
      <div className={styles.twoCol}>
        <Card>
          <div className={styles.sectionHead} style={{ marginBottom: 12 }}>
            <h2 className={styles.sectionTitle}>Forventede faste udgifter</h2>
            <SegmentedToggle
              options={[
                { value: 'month', label: 'Denne måned' },
                { value: 'next', label: 'Næste måned' },
                { value: 'ytd', label: 'År til dato' },
              ]}
              value={period}
              onChange={(v) => setPeriod(v)}
            />
          </div>
          <div className={styles.fastHead}>
            <span className={styles.periodLabel}>{d.periodLabel}</span>
            <span className={styles.fastTotal}>{kr(d.fastTotal)}</span>
          </div>
          {d.categoryBars.map((c) => (
            <div key={c.name} className={styles.catBarRow}>
              <div className={styles.catBarName}>{c.name}</div>
              <div className={styles.catBarProgress}>
                <ProgressBar percent={c.pct} color={c.color} />
              </div>
              <div className={styles.catBarActual}>{kr(c.budget)}</div>
            </div>
          ))}
        </Card>

        <div className={styles.rightCol}>
          <div className={styles.insight}>
            <div className={styles.insightLabel}>Guiden bemærker</div>
            <div className={styles.insightBody}>{d.insight}</div>
            <Link to="/guide" className={styles.insightLink}>
              Spørg guiden →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
